import { useMutation } from '@apollo/client';
import React from 'react';
import { match } from 'ts-pattern';

import { useCompleteFragment } from '@eluve/apollo-client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Icon,
  SimpleTooltip,
  Text,
  useToast,
} from '@eluve/components';
import {
  GlobalBundleBlocker,
  GlobalBundleBlockerTooltip,
} from '@eluve/frontend-feature-prompt-templates';
import { graphql } from '@eluve/graphql.tada';
import { useIsEluveAdmin } from '@eluve/session-helpers';

export interface TogglePromptTemplateActiveActionProps {
  outputTemplateId: string;
  /** Enabled tenant bundles still depending on this template, from the list page. */
  blockers?: GlobalBundleBlocker[];
}

const toggleOutputTemplateActiveMutation = graphql(`
  mutation toggleOutputTemplateActive($id: uuid!, $isActive: Boolean!) {
    updateLlmOutputTemplatesByPk(
      pkColumns: { id: $id }
      _set: { isActive: $isActive }
    ) {
      __typename
      id
      isActive
    }
  }
`);

export const outputTemplateActiveFragment = graphql(`
  fragment OutputTemplateActive on LlmOutputTemplates @_unmask {
    __typename
    id
    isActive
  }
`);

export const ToggleOutputTemplateActiveAction: React.FC<
  TogglePromptTemplateActiveActionProps
> = ({ outputTemplateId, blockers = [] }) => {
  const toast = useToast();
  const isEluveAdmin = useIsEluveAdmin();
  const [updatePromptTemplateIsActive] = useMutation(
    toggleOutputTemplateActiveMutation,
    {
      optimisticResponse: (data) => ({
        updateLlmOutputTemplatesByPk: {
          __typename: 'LlmOutputTemplates' as const,
          id: data.id,
          isActive: data.isActive,
        },
      }),
      onError: () => {
        toast.error('Failed to update Output Template');
      },
    },
  );

  const outputTemplateSettings = useCompleteFragment({
    fragment: outputTemplateActiveFragment,
    key: {
      id: outputTemplateId,
    },
  });

  const isBlocked = outputTemplateSettings.isActive && blockers.length > 0;

  const toggleActive = async () => {
    await updatePromptTemplateIsActive({
      variables: {
        id: outputTemplateId,
        isActive: !outputTemplateSettings.isActive,
      },
    });
  };

  const { label, description, action, Component } = match(
    outputTemplateSettings.isActive,
  )
    .with(true, () => {
      const description = (
        <div>
          <Text>
            Marking this Output Template as inactive will mean users can no
            longer use it for generating LLM outputs
          </Text>
        </div>
      );

      return {
        label: 'Mark this Output Template as inactive',
        description,
        action: 'Deactivate',
        Component: (
          <Icon
            name="PackageMinus"
            size="xl"
            className="text-bg-orange-strong"
          />
        ),
      };
    })
    .otherwise(() => ({
      label: 'Mark this Output Template as active',
      description: (
        <Text>
          Marking this Output Template as active will make it available for
          users
        </Text>
      ),
      action: 'Activate',
      Component: (
        <Icon
          name="PackagePlus"
          size="xl"
          className="text-bg-lightgreen-strong"
        />
      ),
    }));

  if (!isEluveAdmin) {
    return null;
  }

  if (isBlocked) {
    return (
      <GlobalBundleBlockerTooltip blockers={blockers} label={label}>
        {Component}
      </GlobalBundleBlockerTooltip>
    );
  }

  return (
    <AlertDialog>
      <SimpleTooltip label={label}>
        <AlertDialogTrigger asChild>
          <Button variant="outline" aria-label={label}>
            {Component}
          </Button>
        </AlertDialogTrigger>
      </SimpleTooltip>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
          <AlertDialogDescription asChild>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel autoFocus>Cancel</AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={toggleActive}>
            {action}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
